import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import classnames from 'classnames'
import TabbedScrollIndicators from './TabbedScrollIndicators'
import { createLogger } from '@utils/logger'
import { useOverflowNav } from './useOverflowNav'

const log = createLogger('Nav', { prependScope: true })
log.setLevel('info')

export type NavVariant = 'main' | 'sub'

export interface NavHandle {
  scrollLeft: () => void
  scrollRight: () => void
  scrollTo: (offset: number) => void
  scrollToIndex: (index: number) => void
  root: HTMLDivElement | null
  navEl: HTMLElement | null
  listEl: HTMLUListElement | null
  querySelector: (selector: string) => Element | null
}

export interface NavComponentProps {
  variant?: NavVariant
  getNavItemsContent?: () => React.ReactNode | null
  section?: string | null
  navClass?: string
  className?: string
}

const Nav = forwardRef<NavHandle, NavComponentProps>(function Nav(
  { variant = 'main', getNavItemsContent, navClass, className },
  ref
) {
  const rootRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Always use UL as the scroll container
  const scrollRef = listRef as unknown as React.RefObject<HTMLElement>
  const getScrollEl = () => scrollRef.current

  const { showLeft, showRight, scrollByPage, update } = useOverflowNav(scrollRef)

  useImperativeHandle(
    ref,
    (): NavHandle => ({
      scrollLeft: () => {
        const el = getScrollEl()
        if (!el) return
        el.scrollBy({ left: -(el.clientWidth || 0), behavior: 'smooth' })
      },
      scrollRight: () => {
        const el = getScrollEl()
        if (!el) return
        el.scrollBy({ left: el.clientWidth || 0, behavior: 'smooth' })
      },
      scrollTo: (offset: number) => {
        const el = getScrollEl()
        if (!el) return
        el.scrollTo({ left: offset, behavior: 'smooth' })
      },
      scrollToIndex: (index: number) => {
        const ul = listRef.current
        const sc = getScrollEl()
        if (!ul || !sc) return
        const children = Array.from(ul.children)
        if (index < 0 || index >= children.length) return
        const el = children[index] as HTMLElement
        sc.scrollTo({ left: el.offsetLeft, behavior: 'smooth' })
      },
      root: rootRef.current,
      navEl: navRef.current,
      listEl: listRef.current,
      querySelector: (selector: string) => rootRef.current?.querySelector(selector) || null,
    }),
    [variant]
  )

  const items = getNavItemsContent ? getNavItemsContent() : null
  const rootClass = classnames('tabbed-nav__navigation', className, {
    'tabbed-nav__navigation--sub': variant === 'sub',
  })

  return (
    <div ref={rootRef} className={rootClass}>
      <nav ref={navRef} className={navClass || 'tabbed-nav__nav'}>
        <ul ref={listRef} className="tabbed-nav__list" onScroll={update}>
          {items}
        </ul>
      </nav>
      <TabbedScrollIndicators showLeft={showLeft} showRight={showRight} scrollByPage={scrollByPage} />
    </div>
  )
})

export default Nav
